import { useTranslation } from 'react-i18next';
import BaseButton from 'components/atoms/baseButton';
import { convertSecondsToTime, parseDateSafe } from 'utils/timeHelper';
import { format } from 'date-fns';
import common from 'constants/common';
import ReadingCallAudioPlayer from 'components/molecules/reading-call-audio-player';
import { ModuleType } from 'infrastructure/enums';
import { PlayIcon } from 'components/atoms/icons/play';
import { formatAuthorName } from 'utils/formatters';
import { Link } from 'react-router-dom';
import { Routes } from 'router/routes';

import s from './styles.module.scss';
import LinkedReadingsList from './linked-readings-list';
import LinkAction from './link-action';

import type { ILinkCallReadingsTool } from 'infrastructure/hooks/link-call-readings-tool/types';
import type { TableProps } from 'antd';

export const useColumns = (): TableProps<ILinkCallReadingsTool>['columns'] => {
  const { t } = useTranslation();

  return [
    {
      title: t('labels.agent'),
      width: '10%',
      dataIndex: 'agent',
      render: (agent) => agent,
    },
    {
      title: t('labels.callId'),
      width: '20%',
      dataIndex: 'callId',
      render: (callId) => callId,
    },
    {
      title: t('labels.disconnectTime'),
      width: '11%',
      dataIndex: 'disconnectTimestamp',
      render: (disconnectTimestamp) => datePresenter(disconnectTimestamp),
    },
    {
      title: t('labels.callDuration'),
      width: '8%',
      dataIndex: 'duration',
      render: (duration) => convertSecondsToTime(duration),
    },
    {
      title: t('labels.sourcePhone'),
      width: '8%',
      dataIndex: 'sourcePhone',
      render: (sourcePhone) => sourcePhone,
    },
    {
      title: t('labels.patient'),
      width: '20%',
      dataIndex: 'patient',
      render: (_, record) => (
        <Link
          to={Routes.patientGuid(record.patient.guid)}
          className={s['link-to-patient']}
        >
          {formatAuthorName(record.patient)}
        </Link>
      ),
    },
    {
      title: t('labels.linkedReadings'),
      width: '10%',
      render: (_, record) => (
        <LinkedReadingsList
          linkedReadings={record.linkedReadings}
          patientThreshold={record.patientThreshold}
          patientTimezone={record.patient.timezone}
        />
      ),
    },
    {
      title: t('labels.record'),
      width: '3%',
      dataIndex: 'callId',
      align: 'center',
      className: s.callRecord,
      render: (callId, record) => (
        <ReadingCallAudioPlayer
          callId={callId}
          patientGuid={record.patient.guid}
          module={ModuleType.RPM}
          title={record.agent}
          createdAt={record.createdAt}
          thresholds={record.patientThreshold!}
          linkedCallReadings={record.linkedReadings}
          patientTimezone="America/Chicago"
          withThresholds={false}
          withLinkedReadings={false}
        >
          {({ loading, disable, handleDownload }) => (
            <BaseButton
              icon={<PlayIcon />}
              onClick={handleDownload}
              type="secondary"
              loading={loading}
              disabled={disable}
              data-cy="play-linked-call-btn"
            />
          )}
        </ReadingCallAudioPlayer>
      ),
    },
    {
      title: t('labels.action'),
      width: '2%',
      render: (_, record) => (
        <LinkAction callId={record.callId} patient={record.patient} />
      ),
    },
  ];
};

const datePresenter = (date: string | undefined) => {
  if (!date) return '';
  const parsedDate = parseDateSafe(date);
  if (!parsedDate) return '';

  return format(parsedDate, common.dateFormats.full);
};
